import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  History, RefreshCw, Loader2, Play, VolumeX, ShieldAlert,
  User,
} from "lucide-react";
import { API } from "../../config";

// ── Action → display metadata ──────────────────────────────────────
// Keep entries small: icon, verb template (single/plural), colour.
// Missing actions fall through to a neutral default so the widget
// never renders an empty row for unknown events.
const ACTION_META = {
  "sla.watchdog.run_now": {
    icon: Play,
    color: "text-blue-300",
    ring: "border-blue-800/60",
    verb: (meta) => {
      const alerted = meta?.alerted ?? 0;
      if (alerted > 0) {
        return `ha eseguito il watchdog SLA · ${alerted} alert inviat${alerted === 1 ? "o" : "i"}`;
      }
      if (meta?.reason === "no_breach") {
        return "ha eseguito il watchdog SLA · nessuna richiesta sopra soglia";
      }
      return `ha eseguito il watchdog SLA · esito ${meta?.reason || "sconosciuto"}`;
    },
  },
  "sla.quiet_baseline.apply": {
    icon: VolumeX,
    color: "text-amber-300",
    ring: "border-amber-800/60",
    verb: (meta) => {
      const n = meta?.flagged ?? 0;
      if (n === 0) return "ha eseguito la baseline (nessuna richiesta da silenziare)";
      return `ha silenziato ${n} richiest${n === 1 ? "a" : "e"} storiche (soglia ${meta?.threshold_hours || 20}h)`;
    },
  },
};

const DEFAULT_META = {
  icon: ShieldAlert,
  color: "text-gray-400",
  ring: "border-gray-800",
  verb: (_meta, action) => `ha eseguito l'azione: ${action}`,
};

// Actor label shortener — "fabrizio.guerra@telcavoip.eu" → "Fabrizio Guerra"
const prettyActor = (email) => {
  if (!email) return "—";
  const local = email.split("@")[0];
  return local
    .split(/[._-]+/)
    .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s))
    .join(" ");
};

// Timestamp formatter — Italian locale, "oggi HH:MM" if same day.
const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const then = new Date(iso);
    const now = new Date();
    const sameDay =
      then.getFullYear() === now.getFullYear() &&
      then.getMonth() === now.getMonth() &&
      then.getDate() === now.getDate();
    const hh = String(then.getHours()).padStart(2, "0");
    const mm = String(then.getMinutes()).padStart(2, "0");
    if (sameDay) return `oggi ${hh}:${mm}`;
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    if (
      then.getFullYear() === yesterday.getFullYear() &&
      then.getMonth() === yesterday.getMonth() &&
      then.getDate() === yesterday.getDate()
    ) {
      return `ieri ${hh}:${mm}`;
    }
    const dd = String(then.getDate()).padStart(2, "0");
    const mo = String(then.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mo} ${hh}:${mm}`;
  } catch (_) {
    return iso;
  }
};

/**
 * SlaAuditLogWidget — compact "who did what when" timeline for SLA
 * admin actions (Zorzutti CR 03/08/2026).
 *
 * Reads /api/admin/system/audit-log?filter=sla and renders the last 20
 * entries as a scannable timeline. Auto-refresh every 60s so a manager
 * looking at the widget sees new entries appear without a manual
 * reload.
 *
 * Empty state: shows a friendly "no SLA admin action recorded yet".
 * Kept intentionally read-only — no editing/deleting audit entries
 * from the UI (immutability is the whole point of an audit log).
 */
export const SlaAuditLogWidget = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(
        `${API}/admin/system/audit-log?filter=sla&limit=20`
      );
      setItems(r.data?.items || []);
    } catch (e) {
      // Silent-fail: audit log widget is decorative, must not crash
      // the dashboard on a transient network hiccup.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLog();
    const iv = setInterval(fetchLog, 60_000);
    return () => clearInterval(iv);
  }, [fetchLog]);

  return (
    <div
      className="rounded-xl border border-gray-800 bg-[#111] p-5"
      data-testid="sla-audit-log-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-900 border border-gray-800 flex items-center justify-center">
            <History size={18} className="text-gray-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">
              SLA · Audit log azioni admin
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Chi ha eseguito quale azione SLA e quando — ultimi 20 eventi
            </p>
          </div>
        </div>
        <button
          onClick={fetchLog}
          disabled={loading}
          className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition"
          title="Aggiorna"
          data-testid="sla-audit-refresh"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Body */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={20} className="text-gray-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div
          className="text-center py-6 text-gray-500 text-sm"
          data-testid="sla-audit-empty"
        >
          Nessuna azione SLA registrata finora.
          <br />
          <span className="text-gray-600 text-xs">
            Le azioni &laquo;Esegui watchdog ora&raquo; e &laquo;Silenzia storico&raquo; verranno tracciate qui.
          </span>
        </div>
      ) : (
        <ol
          className="space-y-2 max-h-80 overflow-y-auto"
          data-testid="sla-audit-list"
        >
          {items.map((row, idx) => {
            const meta = ACTION_META[row.action] || DEFAULT_META;
            const Icon = meta.icon;
            return (
              <li
                key={`${row.at}-${idx}`}
                className={`flex items-start gap-3 p-3 rounded-lg bg-[#0a0a0a] border ${meta.ring}`}
                data-testid={`sla-audit-row-${row.action}`}
              >
                <div className={`mt-0.5 ${meta.color}`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-white font-medium text-sm inline-flex items-center gap-1">
                      <User size={12} className="text-gray-500" />
                      {prettyActor(row.actor_email)}
                    </span>
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-mono">
                      {row.actor_email}
                    </span>
                  </div>
                  <div className={`text-sm mt-0.5 ${meta.color}`}>
                    {meta.verb(row.metadata, row.action)}
                  </div>
                </div>
                <div className="text-gray-500 text-xs whitespace-nowrap font-mono">
                  {fmtWhen(row.at)}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default SlaAuditLogWidget;
